import type { FilterItem } from '@/components/crud/types';
import dayjs from 'dayjs';

import { BEIJING_TZ } from '@/utils/time';

import type { LiveEditorDraftRow } from '@/components/live-editor/types';

/** 带投诉日的行（live_cases / 编辑草稿共用） */
export type LiveCaseDatedRow = { year?: string | null; date?: string };

export interface YearMonthFilterSlice {
  year: string[];
  month: string[];
}

export interface LiveEditorFilterState extends YearMonthFilterSlice {
  q: string;
  year: string[];
  month: string[];
}

function uniqueSorted(values: string[]): string[] {
  const set = new Set<string>();
  values.forEach((value) => {
    const text = String(value || '').trim();
    if (text) set.add(text);
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'zh-CN'));
}

export function liveCaseYear(row: { year?: string | null; date?: string }): string {
  const fromField = String(row.year || '').trim();
  if (fromField) return fromField;
  const d = String(row.date || '').trim();
  if (d.length >= 4) return d.slice(0, 4);
  return '';
}

export function liveCaseMonth(row: { date?: string }): string {
  const d = String(row.date || '').trim();
  if (d.length < 7) return '';
  const month = parseInt(d.slice(5, 7), 10);
  return Number.isFinite(month) ? String(month) : '';
}

export function normalizeStringArray(value: string | string[] | undefined | null): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || '').trim()).filter(Boolean);
  }
  const one = String(value || '').trim();
  return one ? [one] : [];
}

export function pickDefaultYearMonth(
  rows: Array<{ year?: string | null; date?: string }>,
): { year: string[]; month: string[] } {
  let latest = '';
  rows.forEach((row) => {
    const d = String(row.date || '').trim();
    if (d && (!latest || d > latest)) latest = d;
  });
  const today = dayjs().tz(BEIJING_TZ).format('YYYY-MM-DD');
  if (!latest) {
    return {
      year: [today.slice(0, 4)],
      month: [String(parseInt(today.slice(5, 7), 10))],
    };
  }
  return {
    year: [latest.slice(0, 4)],
    month: [String(parseInt(latest.slice(5, 7), 10))],
  };
}

export function monthsInYear(rows: LiveCaseDatedRow[], year: string): string[] {
  const set = new Set<string>();
  rows.forEach((row) => {
    if (liveCaseYear(row) !== year) return;
    const m = liveCaseMonth(row);
    if (m) set.add(m);
  });
  return Array.from(set).sort((a, b) => Number(a) - Number(b));
}

export function monthsInSelectedYears(rows: LiveCaseDatedRow[], years: string[]): string[] {
  const set = new Set<string>();
  if (!years.length) {
    rows.forEach((row) => {
      const m = liveCaseMonth(row);
      if (m) set.add(m);
    });
  } else {
    years.forEach((year) => {
      monthsInYear(rows, year).forEach((month) => set.add(month));
    });
  }
  return Array.from(set).sort((a, b) => Number(a) - Number(b));
}

export function normalizeYearMonthFilters(
  rows: LiveCaseDatedRow[],
  filters: YearMonthFilterSlice,
): YearMonthFilterSlice {
  const year = normalizeStringArray(filters.year);
  const validMonths = monthsInSelectedYears(rows, year);
  const month = normalizeStringArray(filters.month).filter((item) => validMonths.includes(item));
  return { year, month };
}

/** 年份变更后去掉不在所选年份内的月份 */
export function normalizeLiveEditorFilters(
  rows: LiveEditorDraftRow[],
  filters: LiveEditorFilterState,
): LiveEditorFilterState {
  const { year, month } = normalizeYearMonthFilters(rows, filters);
  return {
    q: filters.q,
    year,
    month,
  };
}

export function buildYearMonthFilterItems(
  rows: LiveCaseDatedRow[],
  filters: YearMonthFilterSlice,
): FilterItem[] {
  const yearOptions = uniqueSorted(rows.map((row) => liveCaseYear(row)))
    .sort((a, b) => b.localeCompare(a))
    .map((year) => ({ label: `${year} 年`, value: year }));

  const monthValues = monthsInSelectedYears(rows, normalizeStringArray(filters.year));
  const monthOptions = monthValues.map((month) => ({ label: `${month} 月`, value: month }));

  return [
    {
      key: 'year',
      type: 'select',
      placeholder: '年份',
      width: '156px',
      clearable: true,
      multiple: true,
      collapseTags: true,
      options: yearOptions,
    },
    {
      key: 'month',
      type: 'select',
      placeholder: '月份',
      width: '136px',
      clearable: true,
      multiple: true,
      collapseTags: true,
      options: monthOptions,
    },
  ];
}

export function matchesYearMonthFilter(row: LiveCaseDatedRow, filters: YearMonthFilterSlice): boolean {
  const years = normalizeStringArray(filters.year);
  const months = normalizeStringArray(filters.month);
  if (years.length && !years.includes(liveCaseYear(row))) return false;
  if (months.length && !months.includes(liveCaseMonth(row))) return false;
  return true;
}

export function buildLiveEditorFilterItems(
  rows: LiveEditorDraftRow[],
  filters: LiveEditorFilterState,
): FilterItem[] {
  return [
    ...buildYearMonthFilterItems(rows, filters),
    {
      key: 'q',
      type: 'input',
      placeholder: '筛选品名、料号、异常、负责人',
      width: '260px',
    },
  ];
}

export function filterLiveEditorRows(
  rows: LiveEditorDraftRow[],
  filters: LiveEditorFilterState,
): LiveEditorDraftRow[] {
  const q = filters.q.trim().toLowerCase();
  return rows.filter((row) => {
    if (!matchesYearMonthFilter(row, filters)) return false;
    if (!q) return true;
    const hay = [row.patch.pm, row.patch.lh, row.patch.yc, row.patch.fzr, row.patch.deptP]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  });
}
